import {
  getAPITaskParamsFromAttempt,
  TaskCreationFailedBannerList,
} from '@github-ui/agent-sessions/components/TaskCreationFailedBanner'
import {ConsecutiveCAPI401sProvider} from '@github-ui/agent-sessions/contexts/ConsecutiveCAPI401sContext'
import {useIntentDetectionContext} from '@github-ui/agent-sessions/contexts/IntentDetectionContext'
import {IntentDetectionEnabledProvider} from '@github-ui/agent-sessions/contexts/IntentDetectionEnabledContext'
import type {DropdownRepository} from '@github-ui/agent-sessions/hooks/useCopilotRepoFetching'
import type {IntentDetectionMode} from '@github-ui/agent-sessions/types/intent-detection-mode'
import type {SelectedAgent} from '@github-ui/agent-sessions/types/session'
import {uuid} from '@github-ui/agent-sessions/utils/uuid'
import type {ChatInputRef} from '@github-ui/copilot-chat/components/ChatInput'
import CopilotIconAnimation from '@github-ui/copilot-chat/components/CopilotIconAnimation'
import CopilotSuggestions from '@github-ui/copilot-chat/components/CopilotSuggestions'
import {LegalDisclaimer} from '@github-ui/copilot-chat/components/LegalDisclaimer'
import {EMBEDDED_MENU_PORTAL_CLASS, MenuPortalContainer} from '@github-ui/copilot-chat/components/PortalContainerUtils'
import {useEntitlement} from '@github-ui/copilot-chat/components/quota/EntitlementContext'
import {useQuotaBanner} from '@github-ui/copilot-chat/hooks/use-quota-banner'
import {isPaidUser} from '@github-ui/copilot-chat/utils/copilot-chat-entitlement'
import {type CopilotChatCommandType, ModeSupportName} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {copilotFeatureFlags} from '@github-ui/copilot-chat/utils/copilot-feature-flags'
import {copilotLocalStorage} from '@github-ui/copilot-chat/utils/copilot-local-storage'
// eslint-disable-next-line no-restricted-imports
import {useChatState} from '@github-ui/copilot-chat/utils/CopilotChatContext'
import {useChatManager} from '@github-ui/copilot-chat/utils/CopilotChatManagerContext'
import {AgentsList} from '@github-ui/dashboard-lists/components/AgentsList'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {sendEvent} from '@github-ui/hydro-analytics'
import ProductAnnouncement from '@github-ui/product-announcement/components/ProductAnnouncement'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {useNavigate} from '@github-ui/use-navigate'
import {clsx} from 'clsx'
import {useCallback, useEffect, useRef, useState} from 'react'

import {useFailedTasks} from '../../contexts/FailedTasksContext'
import {ChatInputContainer} from '../ChatInputContainer'
import {CopilotEmptyStateAnimation} from '../CopilotEmptyStateAnimation'
import {CommandStartersAndSuggestions} from './CommandStartersAndSuggestions'
import styles from './NewConversation.module.css'
import {NewConversationBanners} from './NewConversationBanners'
import {
  getProductAnnouncementId,
  handleProductAnnouncementBannerTracking,
  handleProductAnnouncementModalTracking,
} from './product-announcement'
import {SuggestionPills} from './SuggestionPills'
import {TasksInChatInput} from './TasksInChatInput'

interface NewConversationProps {
  textAreaRef: React.RefObject<HTMLTextAreaElement | null>
  handleUserSubmit: (content: string) => Promise<void>
  nextMessageIndex: number
  /** Force icon-only mode in the toolbar, useful for container-based responsive behavior */
  forceIconOnly?: boolean
  /** Whether this is the embedded immersive view (shows URL-based suggestions instead of command starters) */
  isEmbedded?: boolean
}

export function NewConversation({
  textAreaRef,
  handleUserSubmit,
  nextMessageIndex,
  forceIconOnly,
  isEmbedded,
}: NewConversationProps) {
  const state = useChatState()
  const manager = useChatManager()
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLFormElement>(null)
  const hasSubmittedChatRef = useRef(false)
  const [hasEmbeddedOverflow, setHasEmbeddedOverflow] = useState(false)

  // Intent detection POC
  const isUsingIntentDetection = isFeatureEnabled('copilot_intent_detection')
  const {setTaskCreationRequest} = useIntentDetectionContext()
  const navigate = useNavigate()

  const {chatQuotaExceeded, isLicensedLimited, tokenBasedBillingEnabled} = useEntitlement()
  const quotaBanner = useQuotaBanner()
  const isInputDisabled = tokenBasedBillingEnabled
    ? quotaBanner.show && quotaBanner.isBlocking // The input is disabled when users have exceeded their quota (i.e. the banner is in blocking mode)
    : isLicensedLimited && chatQuotaExceeded // The input is disabled only when users have a limited license AND have exceeded their quota
  const [chatCommand, setChatCommand] = useState<CopilotChatCommandType | null>(null)
  const [activeModeName, setActiveModeName] = useState<string>(() => {
    const savedModeName = copilotLocalStorage.getActiveMode()
    return savedModeName ?? ModeSupportName.Ask
  })

  const chatInputRef = useRef<ChatInputRef>(null)

  // TODO: Pass to `getProductAnnouncementId` whether the user needs onboarding
  const productAnnouncementId = getProductAnnouncementId({needsOnboarding: false})
  const {plan: copilotPlan} = useEntitlement()

  const isMissionControlEnabled = state.agentsEnabled
  /* eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags --
     TODO: Migrate to isFeatureEnabled from @github-ui/feature-flags. See
     https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
  const showProPlusAnimation = useFeatureFlag('copilot_pro_plus_animation')

  const {failedTasks, setFailedTasks, createTask} = useFailedTasks()

  const handleRetry = useCallback(
    (attemptIndex: number) => {
      const attempt = failedTasks[attemptIndex]
      if (attempt) {
        setFailedTasks(prev => prev.filter((_, index) => index !== attemptIndex))
        createTask(getAPITaskParamsFromAttempt(attempt))
      }
    },
    [failedTasks, setFailedTasks, createTask],
  )

  const handleDismiss = useCallback(
    (attemptIndex: number) => {
      setFailedTasks(prev => prev.filter((_, index) => index !== attemptIndex))
    },
    [setFailedTasks],
  )

  const handleSubmit = useCallback(
    async (content: string) => {
      hasSubmittedChatRef.current = true
      await handleUserSubmit(content)
    },
    [handleUserSubmit],
  )

  const handleIntentDetectionSubmit = async (
    content: string,
    selectedRepo: DropdownRepository | null,
    selectedBranch: string,
    selectedAgent: SelectedAgent,
    selectedMode?: IntentDetectionMode,
    modelId?: string,
    createPullRequest?: boolean,
  ): Promise<void> => {
    const trimmedContent = content.trim()
    if (trimmedContent === '' || !selectedBranch || selectedBranch.trim() === '') return
    if (selectedRepo) {
      const clientGeneratedId = uuid()
      setTaskCreationRequest({
        nwo: selectedRepo.nameWithOwner,
        problemStatement: trimmedContent,
        baseRef: selectedBranch,
        model: modelId,
        agentId: selectedAgent.topLevelAgentId,
        customAgent: selectedAgent.subAgentSlug,
        createPullRequest,
        mode: selectedMode,
        clientGeneratedId,
      })

      navigate(`/copilot/t/${selectedRepo.nameWithOwner}/new`)
    } else {
      throw new Error('Intent detection requires a repository selection.')
    }
    return Promise.resolve()
  }

  useEffect(() => {
    // Log feature flag values when NewConversation first renders
    sendEvent('dotcom_chat.activate', {
      target: 'NEW_CONVERSATION_VIEW_LOADED',
      defaultToTask: copilotFeatureFlags.defaultToAgentInput,
      defaultToTaskControl: copilotFeatureFlags.defaultToAgentControl,
      askModeDropdown: copilotFeatureFlags.askModeDropdown,
    })

    // Log bounce event if user leaves without submitting a chat
    return () => {
      if (!hasSubmittedChatRef.current) {
        sendEvent('dotcom_chat.activate', {
          target: 'NEW_CONVERSATION_BOUNCE',
          mode: 'immersive',
          bounceToUrl: document.location.href,
          defaultToTask: copilotFeatureFlags.defaultToAgentInput,
          defaultToTaskControl: copilotFeatureFlags.defaultToAgentControl,
        })
      }
    }
  }, [])

  // Regenerate suggestions for embedded mode when component mounts (e.g., new conversation)
  useEffect(() => {
    if (!isEmbedded) return

    const suggestionContext = state.context?.[0] ?? state.currentRepository
    if (suggestionContext) {
      manager.generateSuggestions(suggestionContext)
    }

    return () => {
      manager.clearSuggestions()
    }
  }, [isEmbedded, manager, state.context, state.currentRepository, state.selectedThreadID])

  useEffect(() => {
    if (!isEmbedded) return

    const scrollElement = scrollContainerRef.current
    if (!scrollElement) return

    let frameId = 0

    const updateEmbeddedOverflow = () => {
      if (frameId) cancelAnimationFrame(frameId)

      frameId = requestAnimationFrame(() => {
        const hasOverflow = scrollElement.scrollHeight - scrollElement.clientHeight > 1
        setHasEmbeddedOverflow(prev => (prev === hasOverflow ? prev : hasOverflow))
      })
    }

    updateEmbeddedOverflow()

    let resizeObserver: ResizeObserver | undefined
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(updateEmbeddedOverflow)
      resizeObserver.observe(scrollElement)

      const innerContainer = scrollElement.firstElementChild
      if (innerContainer instanceof HTMLElement) {
        resizeObserver.observe(innerContainer)
      }
    }

    return () => {
      if (frameId) cancelAnimationFrame(frameId)
      resizeObserver?.disconnect()
    }
  }, [isEmbedded])

  const hasAnyReferences = state.currentReferences.length > 0

  return (
    <>
      <div
        data-testid="new-conversation-scroll-container"
        ref={scrollContainerRef}
        className={clsx(styles.scrollContainer, {
          [styles.embeddedScrollContainer]: isEmbedded,
          [styles.embeddedScrollContainerOverflowing]: isEmbedded && hasEmbeddedOverflow,
        })}
      >
        {!isEmbedded && (
          <MenuPortalContainer className={state.mode === 'assistive' ? EMBEDDED_MENU_PORTAL_CLASS : undefined} />
        )}
        <div className={clsx(styles.container, {[styles.embeddedContainer]: isEmbedded})}>
          {isEmbedded && (
            <div className={styles.embeddedDisclaimer}>
              <LegalDisclaimer />
            </div>
          )}
          <div className={styles.banner}>
            <NewConversationBanners />
          </div>
          <div
            className={clsx(styles.main, {
              [styles.withTaskList]: isMissionControlEnabled && !isEmbedded,
              [styles.embeddedMain]: isEmbedded,
            })}
          >
            <div className={clsx(styles.content, {[styles.embeddedContent]: isEmbedded})}>
              <div className={clsx(styles.innerContainer, {[styles.embeddedInnerContainer]: isEmbedded})}>
                <h1 className="sr-only">Copilot Chat</h1>

                <div
                  className={clsx(styles.inputAndStartersContainer, {
                    [styles.embeddedInputContainer]: isEmbedded,
                  })}
                >
                  {showProPlusAnimation ? <CopilotEmptyStateAnimation /> : <CopilotIconAnimation hidden />}

                  {/* For embedded, show suggestions above input */}
                  {isEmbedded && !isInputDisabled && !isUsingIntentDetection && (
                    <div className={styles.embeddedSuggestions}>
                      <CopilotSuggestions />
                    </div>
                  )}

                  {/* For non-embedded, render the input inside the scroll container */}
                  {!isEmbedded &&
                    (!isUsingIntentDetection ? (
                      <ChatInputContainer
                        chatInputRef={chatInputRef}
                        textAreaRef={textAreaRef}
                        handleUserSubmit={handleSubmit}
                        nextMessageIndex={nextMessageIndex}
                        onChatCommandChange={setChatCommand}
                        isNewConversation
                        containerRef={containerRef}
                        slashCommandsEnabled
                        showDisabledInputOnQuotaExceeded
                        onClickMode={setActiveModeName}
                        activeModeName={activeModeName}
                        forceIconOnly={forceIconOnly}
                      />
                    ) : (
                      <IntentDetectionEnabledProvider
                        isUsingIntentDetection={isFeatureEnabled('copilot_intent_detection')}
                      >
                        <TasksInChatInput
                          handleIntentDetectionSubmit={handleIntentDetectionSubmit}
                          agentQuotaSnapshot={state.agentQuotaSnapshot}
                        />
                      </IntentDetectionEnabledProvider>
                    ))}

                  {/* For non-embedded, show contextual suggestions when references are attached */}
                  {!isEmbedded &&
                    !isInputDisabled &&
                    !isUsingIntentDetection &&
                    hasAnyReferences &&
                    copilotFeatureFlags.copilotImmersiveSuggestionPills && (
                      <div className={styles.commandStartersContainer}>
                        <SuggestionPills
                          chatInputRef={chatInputRef}
                          textAreaRef={textAreaRef}
                          onChatCommandChange={setChatCommand}
                          chatCommand={chatCommand}
                          containerRef={containerRef}
                        />
                      </div>
                    )}

                  {/* For non-embedded, show command starters below input when no references are attached,
                      or as a fallback when suggestion pills are not enabled */}
                  {!isEmbedded &&
                    !isInputDisabled &&
                    !isUsingIntentDetection &&
                    (!hasAnyReferences || !copilotFeatureFlags.copilotImmersiveSuggestionPills) &&
                    copilotFeatureFlags.chatInputCommands && (
                      <div className={styles.commandStartersContainer}>
                        <CommandStartersAndSuggestions
                          chatCommand={chatCommand}
                          chatInputRef={chatInputRef}
                          textAreaRef={textAreaRef}
                          containerRef={containerRef}
                          paidUser={isPaidUser(copilotPlan)}
                          activeModeName={activeModeName}
                        />
                      </div>
                    )}
                </div>

                {productAnnouncementId && (
                  <ProductAnnouncement
                    id="copilot-immersive-v1"
                    announcementId={productAnnouncementId}
                    shouldShowTabCtas={isPaidUser(copilotPlan)}
                    shouldShowMainCta={!isPaidUser(copilotPlan)}
                    useLocalStorage
                    onBannerEvent={event => handleProductAnnouncementBannerTracking(productAnnouncementId, event)}
                    onModalEvent={(event, data) => {
                      handleProductAnnouncementModalTracking(productAnnouncementId, event, data)

                      if (productAnnouncementId === 'ccaOnboarding') {
                        if (event === 'main_cta1_clicked') {
                          // TODO: Trigger "Get started" flow
                        } else if (event === 'tab_cta2_clicked') {
                          switch (data?.tabIndex) {
                            case 1:
                              // TODO: Trigger "Start with agents" flow
                              break
                            case 2:
                              // TODO: Trigger "Create a custom agent" flow
                              break
                          }
                        }
                      }
                    }}
                  />
                )}

                <TaskCreationFailedBannerList
                  attempts={failedTasks}
                  onRetry={handleRetry}
                  onDismiss={handleDismiss}
                  className={styles.failedTasksContainer}
                />

                {isMissionControlEnabled && !isEmbedded && (
                  <div className={styles.agentListContainer}>
                    <IntentDetectionEnabledProvider
                      isUsingIntentDetection={isFeatureEnabled('copilot_intent_detection')}
                    >
                      <ConsecutiveCAPI401sProvider>
                        <AgentsList showViewAll title="Recent agent sessions" initialResultCount={5} />
                      </ConsecutiveCAPI401sProvider>
                    </IntentDetectionEnabledProvider>
                  </div>
                )}
              </div>
            </div>
          </div>
          {!isEmbedded && (
            <div className={styles.footer}>
              <LegalDisclaimer />
            </div>
          )}
        </div>
      </div>
    </>
  )
}
